import React, { useState, useEffect, useRef } from 'react';
import { IMDFData, IMDFFeature } from '../../types/imdf.types';

interface IMDFFileListProps {
  imdfData: IMDFData | null;
  onFeatureClick: (feature: GeoJSON.Feature) => void;
  selectedFeature?: GeoJSON.Feature | null;
  expandedFiles?: Set<string>;
  onExpandedFilesChange?: (expandedFiles: Set<string>) => void;
  className?: string;
}

const IMDFFileList: React.FC<IMDFFileListProps> = ({
  imdfData,
  onFeatureClick,
  selectedFeature,
  expandedFiles: propExpandedFiles,
  onExpandedFilesChange,
  className = '',
}) => {
  const [internalExpandedFiles, setInternalExpandedFiles] = useState<Set<string>>(new Set());
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  
  // Use external state if provided, otherwise use internal state
  const expandedFiles = propExpandedFiles || internalExpandedFiles;
  const setExpandedFiles = onExpandedFilesChange || setInternalExpandedFiles;
  const selectedFeatureRef = useRef<HTMLDivElement>(null);

  // 當 selectedFeature 改變時，自動展開對應檔案並滾動到該特徵
  useEffect(() => {
    if (selectedFeature && imdfData) {
      const sourceFile = selectedFeature.properties?._sourceFile;
      if (sourceFile) {
        // 自動展開包含該特徵的檔案
        if (onExpandedFilesChange) {
          onExpandedFilesChange(new Set([...expandedFiles, sourceFile]));
        } else {
          setInternalExpandedFiles(prev => new Set([...prev, sourceFile]));
        }
        
        // 滾動到選中的特徵
        setTimeout(() => {
          if (selectedFeatureRef.current) {
            selectedFeatureRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    }
  }, [selectedFeature, imdfData]);

  if (!imdfData || imdfData.files.length === 0) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">分檔資訊</h3>
        <div className="text-center py-8 text-gray-500">
          沒有可顯示的檔案資訊
        </div>
      </div>
    );
  }

  // 處理多語言 name
  const getName = (nameProperty: any): string => {
    if (typeof nameProperty === 'string') return nameProperty;
    if (typeof nameProperty === 'object' && nameProperty !== null) {
      return nameProperty['zh'] || nameProperty['en'] || Object.values(nameProperty)[0] || 'Unknown';
    }
    return 'Unknown';
  };

  // 切換檔案展開狀態
  const toggleFileExpansion = (fileName: string) => {
    const newExpanded = new Set(expandedFiles);
    if (newExpanded.has(fileName)) {
      newExpanded.delete(fileName);
    } else {
      newExpanded.add(fileName);
    }
    
    if (onExpandedFilesChange) {
      onExpandedFilesChange(newExpanded);
    } else {
      setInternalExpandedFiles(newExpanded);
    }
  };

  // 切換 feature 詳情展開狀態
  const toggleFeatureExpansion = (featureId: string) => {
    const newExpanded = new Set(expandedFeatures);
    if (newExpanded.has(featureId)) {
      newExpanded.delete(featureId);
    } else {
      newExpanded.add(featureId);
    }
    setExpandedFeatures(newExpanded);
  };

  // 處理 feature 點擊
  const handleFeatureClick = (feature: GeoJSON.Feature) => {
    onFeatureClick(feature);
  };

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">分檔資訊</h3>
            <p className="text-sm text-gray-500 mt-1">
              {imdfData.files.length} 個 GeoJSON 檔案，共 {imdfData.totalFeatures} 個 features
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                if (onExpandedFilesChange) {
                  onExpandedFilesChange(new Set());
                } else {
                  setInternalExpandedFiles(new Set());
                }
              }}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              全部收合
            </button>
            <button
              onClick={() => {
                const allFiles = new Set(imdfData.files.map(f => f.name));
                if (onExpandedFilesChange) {
                  onExpandedFilesChange(allFiles);
                } else {
                  setInternalExpandedFiles(allFiles);
                }
              }}
              className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
            >
              全部展開
            </button>
          </div>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {imdfData.files.map((file) => (
          <div key={file.name} className="border-b border-gray-100 last:border-b-0">
            {/* 檔案標題列 */}
            <div
              className="px-6 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
              onClick={() => toggleFileExpansion(file.name)}
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      expandedFiles.has(file.name) ? 'rotate-90' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">{file.name.replace('.geojson', '').split('/').pop()}</div>
                  <div className="text-xs text-gray-500">{file.featureCount} features</div>
                </div>
              </div>
              <div className="text-xs text-gray-400">
                {expandedFiles.has(file.name) ? '收起' : '展開'}
              </div>
            </div>

            {/* Feature 列表 */}
            {expandedFiles.has(file.name) && (
              <div className="px-6 pb-3">
                <div className="space-y-2">
                  {file.features.map((feature, index) => {
                    const featureId = String(feature.id || `${file.name}-${index}`);
                    const name = getName(feature.properties?.name) || featureId;
                    const category = feature.properties?.category || 'Unknown';
                    const stoConfirm = feature.properties?.stoConfirm === true;
                    const isSelected = selectedFeature && (selectedFeature.id === feature.id || 
                      (selectedFeature.properties?._sourceFile === feature.properties?._sourceFile && 
                       selectedFeature.properties?.name === feature.properties?.name));
                    
                    return (
                      <div 
                        key={featureId} 
                        ref={isSelected ? selectedFeatureRef : null}
                        className={`${isSelected ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'} rounded-md transition-colors duration-200`}
                      >
                        {/* Feature 基本資訊列 */}
                        <div className="px-4 py-3 flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3">
                              {isSelected && (
                                <span className="flex items-center justify-center w-4 h-4 bg-blue-500 text-white rounded-full text-xs">
                                  📍
                                </span>
                              )}
                              <button
                                onClick={() => handleFeatureClick(feature)}
                                className={`hover:underline text-sm font-medium truncate ${
                                  isSelected ? 'text-blue-800 font-semibold' : 'text-blue-600 hover:text-blue-800'
                                }`}
                              >
                                {name}
                              </button>
                              <span className={`inline-flex items-center text-xs ${
                                stoConfirm ? 'text-green-600' : 'text-red-500'
                              }`}>
                                {stoConfirm ? '✅' : '❌'}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                              <span>ID: {featureId}</span>
                              <span>分類: {category}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => toggleFeatureExpansion(featureId)}
                            className="ml-2 text-xs text-gray-400 hover:text-gray-600"
                          >
                            {expandedFeatures.has(featureId) ? '隱藏詳情' : '查看詳情'}
                          </button>
                        </div>

                        {/* Feature JSON 詳情 */}
                        {expandedFeatures.has(featureId) && (
                          <div className="px-4 pb-3">
                            <div className="border-t border-gray-200 pt-3">
                              <h5 className="text-xs font-medium text-gray-700 mb-2">原始 JSON 資料:</h5>
                              <pre className="text-xs bg-gray-100 p-3 rounded border overflow-x-auto text-gray-800">
                                {JSON.stringify(feature, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IMDFFileList;